import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { 
  User, 
  Mail, 
  Crown,
  Shield,
  Lock,
  Loader2,
  Eye,
  EyeOff,
  Check,
  LogOut,
  Bell,
  BellOff
} from 'lucide-react';
import { toast } from 'sonner';
import useNotifications from '../hooks/useNotifications';

const Profile = () => {
  const navigate = useNavigate();
  const { user, isPremium, isAdmin, updatePassword, logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Notifications
  const { isSupported, isGranted, permission, requestPermission, sendNotification } = useNotifications();
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);

  useEffect(() => {
    // Check if notifications are enabled in localStorage
    const stored = localStorage.getItem('coinpulse_notifications');
    setNotificationsEnabled(stored === 'true' && isGranted);
  }, [isGranted]);

  const handleNotificationToggle = async (checked) => {
    if (checked) {
      const granted = await requestPermission();
      if (granted) {
        setNotificationsEnabled(true);
        localStorage.setItem('coinpulse_notifications', 'true');
        toast.success('Notifications enabled!');
        // Send test notification
        sendNotification('CoinPulse Notifications Active', {
          body: 'You will now receive alerts for new trading signals.',
        });
      } else {
        toast.error('Please allow notifications in your browser settings');
        setNotificationsEnabled(false);
      }
    } else {
      setNotificationsEnabled(false);
      localStorage.setItem('coinpulse_notifications', 'false');
      toast.success('Notifications disabled');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await updatePassword(currentPassword, newPassword);
      toast.success('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      const message = error.response?.data?.detail?.message || error.response?.data?.detail || 'Failed to update password';
      toast.error(typeof message === 'object' ? JSON.stringify(message) : message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6 animate-fade-in-up">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Profile
          </h1>
          <p className="text-sm text-[#A1A1AA] mt-1">
            Manage your account settings
          </p>
        </div>

        {/* Account Info */}
        <Card className="border-[#1F1F1F] bg-[#0A0A0A]">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <User className="w-5 h-5 text-[#00FF94]" />
              Account Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-black/50 rounded-lg border border-[#1F1F1F]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#1F1F1F] flex items-center justify-center">
                  <Mail className="w-5 h-5 text-[#A1A1AA]" />
                </div>
                <div>
                  <p className="text-xs text-[#737373] uppercase tracking-wider">Email</p>
                  <p className="text-white font-medium">{user?.email}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-black/50 rounded-lg border border-[#1F1F1F]">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  isPremium ? 'bg-[#6366F1]/10' : 'bg-[#1F1F1F]'
                }`}>
                  <Crown className={`w-5 h-5 ${isPremium ? 'text-[#6366F1]' : 'text-[#A1A1AA]'}`} />
                </div>
                <div>
                  <p className="text-xs text-[#737373] uppercase tracking-wider">Account Type</p>
                  <div className="flex items-center gap-2">
                    {isPremium ? (
                      <Badge className="bg-[#6366F1]/10 text-[#6366F1] border border-[#6366F1]/20 font-mono text-xs uppercase tracking-wider">
                        Premium
                      </Badge>
                    ) : (
                      <Badge className="bg-white/5 text-white/60 border border-white/10 font-mono text-xs uppercase tracking-wider">
                        Free
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              {!isPremium && (
                <Button 
                  size="sm"
                  className="bg-[#6366F1] hover:bg-[#6366F1]/90 text-white"
                  onClick={() => toast.info('Premium upgrade coming soon!')}
                  data-testid="upgrade-btn"
                >
                  Upgrade
                </Button>
              )}
            </div>

            {isAdmin && (
              <div className="flex items-center justify-between p-4 bg-black/50 rounded-lg border border-[#1F1F1F]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#00FF94]/10 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-[#00FF94]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#737373] uppercase tracking-wider">Role</p>
                    <Badge className="bg-[#00FF94]/10 text-[#00FF94] border border-[#00FF94]/20 font-mono text-xs uppercase tracking-wider">
                      Administrator
                    </Badge>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card className="border-[#1F1F1F] bg-[#0A0A0A]">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#00FF94]" />
              Notifications
            </CardTitle>
            <CardDescription className="text-[#A1A1AA]">
              Get notified when new trading signals are posted
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-black/50 rounded-lg border border-[#1F1F1F]">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  notificationsEnabled ? 'bg-[#00FF94]/10' : 'bg-[#1F1F1F]'
                }`}>
                  {notificationsEnabled ? (
                    <Bell className="w-5 h-5 text-[#00FF94]" />
                  ) : (
                    <BellOff className="w-5 h-5 text-[#A1A1AA]" />
                  )}
                </div>
                <div>
                  <p className="text-white font-medium">Browser Notifications</p>
                  <p className="text-xs text-[#737373]">
                    {!isSupported 
                      ? 'Not supported in this browser'
                      : notificationsEnabled 
                        ? 'You will receive alerts for new signals'
                        : 'Enable to get notified of new signals'
                    }
                  </p>
                </div>
              </div>
              <Switch
                checked={notificationsEnabled}
                onCheckedChange={handleNotificationToggle}
                disabled={!isSupported}
                className="data-[state=checked]:bg-[#00FF94]"
                data-testid="notifications-switch"
              />
            </div>
            
            {notificationsEnabled && (
              <div className="p-3 rounded-lg bg-[#00FF94]/5 border border-[#00FF94]/20">
                <div className="flex items-center gap-2 text-[#00FF94] text-sm">
                  <Check className="w-4 h-4" />
                  <span>Notifications are active. You'll be alerted when new signals are posted.</span>
                </div>
              </div>
            )}

            {permission === 'denied' && (
              <div className="p-3 rounded-lg bg-[#FF0055]/5 border border-[#FF0055]/20">
                <p className="text-[#FF0055] text-sm">
                  Notifications are blocked. Please enable them in your browser settings.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card className="border-[#1F1F1F] bg-[#0A0A0A]">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Lock className="w-5 h-5 text-[#00FF94]" />
              Change Password
            </CardTitle>
            <CardDescription className="text-[#A1A1AA]">
              Update your password to keep your account secure
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="currentPassword" className="text-[#EDEDED]">Current Password</Label>
                <div className="relative">
                  <Input
                    id="currentPassword"
                    type={showCurrentPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="bg-black border-[#1F1F1F] focus:border-[#00FF94] text-white placeholder:text-[#737373] pr-10"
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373] hover:text-white transition-colors"
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPassword" className="text-[#EDEDED]">New Password</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="bg-black border-[#1F1F1F] focus:border-[#00FF94] text-white placeholder:text-[#737373] pr-10"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373] hover:text-white transition-colors"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-[#EDEDED]">Confirm New Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-black border-[#1F1F1F] focus:border-[#00FF94] text-white placeholder:text-[#737373]"
                  data-testid="confirm-new-password-input"
                />
              </div>

              {/* Password requirements */}
              <div className="text-xs text-[#737373] space-y-1">
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center ${newPassword.length >= 6 ? 'bg-[#00FF94]/20 text-[#00FF94]' : 'bg-[#1F1F1F]'}`}>
                    {newPassword.length >= 6 && <Check className="w-3 h-3" />}
                  </div>
                  <span className={newPassword.length >= 6 ? 'text-[#00FF94]' : ''}>At least 6 characters</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center ${newPassword === confirmPassword && newPassword.length > 0 ? 'bg-[#00FF94]/20 text-[#00FF94]' : 'bg-[#1F1F1F]'}`}>
                    {newPassword === confirmPassword && newPassword.length > 0 && <Check className="w-3 h-3" />}
                  </div>
                  <span className={newPassword === confirmPassword && newPassword.length > 0 ? 'text-[#00FF94]' : ''}>Passwords match</span>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-[#00FF94] text-black hover:bg-[#00FF94]/90 font-bold"
                disabled={loading}
                data-testid="change-password-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update Password'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Logout */}
        <Card className="border-[#1F1F1F] bg-[#0A0A0A]">
          <CardContent className="p-4">
            <Button 
              variant="outline"
              onClick={handleLogout}
              className="w-full border-[#FF0055]/30 text-[#FF0055] hover:bg-[#FF0055]/10 hover:text-[#FF0055]"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Profile;
